import { useCallback } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { apiUrl } from "@/lib/api-config";
import {
  postContractReviewSse,
  type ContractReviewSseCallbacks,
  type ReviewStreamCompletion,
} from "@/lib/contract-review-sse";
import { readSupportingDocumentsAsBase64 } from "@/lib/supporting-documents-payload";
import type { SelectedDriveFile } from "@/components/wizard/GoogleDrivePicker";
import {
  downloadGoogleDriveFile,
  googleDriveDownloadToFile,
} from "@/lib/api/google-drive";
import { REVIEW_USER_ERROR } from "@/lib/review-user-errors";
import type { ReviewFormData } from "@/types";

function readFileAsBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      if (e.target?.result) resolve(e.target.result as string);
      else reject(new Error("Failed to read file"));
    };
    reader.onerror = () => reject(new Error("Failed to read file"));
    reader.readAsDataURL(file);
  });
}

export function useNewVersion() {
  const { getAccessToken } = useAuth();

  const createNewVersion = useCallback(
    async (
      sourceReviewId: string,
      file: File | null,
      driveFile: SelectedDriveFile | null,
      formData: ReviewFormData,
      sseCallbacks?: ContractReviewSseCallbacks,
    ): Promise<ReviewStreamCompletion | null> => {
      let sourceFile: File;

      if (driveFile) {
        const downloaded = await downloadGoogleDriveFile({
          fileId: driveFile.id,
          resourceKey: driveFile.resourceKey,
          accessToken: driveFile.accessToken,
        });
        sourceFile = googleDriveDownloadToFile(downloaded);
      } else if (file) {
        sourceFile = file;
      } else {
        throw new Error(REVIEW_USER_ERROR.selectFileToUpload);
      }

      const fileBase64 = await readFileAsBase64(sourceFile);
      const supportingDocuments = await readSupportingDocumentsAsBase64(
        formData.supportingDocs ?? []
      );

      const requestData = {
        fileBase64,
        fileName: sourceFile.name,
        backgroundInfo: formData.backgroundInfo || "No background information provided.",
        agentInstructions: formData.agentInstructions || "",
        contractType: formData.contractType,
        userRole: formData.userRole,
        jurisdiction: formData.jurisdiction,
        skipResearch: formData.skipResearch !== false,
        ...(supportingDocuments.length > 0 ? { supportingDocuments } : {}),
      };

      const accessToken = await getAccessToken();
      const url = apiUrl(
        `/review-contract/${encodeURIComponent(sourceReviewId)}/new-version`,
      );
      return postContractReviewSse(url, requestData, accessToken, sseCallbacks);
    },
    [getAccessToken],
  );

  return { createNewVersion };
}
